/**
 * React Query hook for Firm User List (Global)
 * Provides automatic caching, background sync, and error handling
 * Matches the behavior of useFirmUserList hook
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { listUsers } from 'services/users/userServices';
import { totalCountEvent } from 'services/generalServices';
import { sortByKey } from 'utils/utils';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches firm users data
 * Always loads the full list by getting total count first, then fetching all items
 * Filters and sorts users and groups same as useFirmUserList
 *
 * @param {Object} params - Query parameters
 * @param {string} params.status - User status filter (default: 'ACTIVE')
 * @param {number} params.limit - Limit for fetching users
 * @param {string} params.pagination_token - Pagination token (default: '')
 * @param {string} params.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} params.keyWord - Search keyword (default: '')
 * @param {string} params.next - Next page token (default: '')
 * @param {boolean} params.is_include_contacts - Include contacts (default: false)
 * @param {boolean} params.is_detailed - Include detailed info (default: false)
 * @param {boolean} params.is_confirmed_user - Filter confirmed users (default: true)
 * @param {boolean} params.is_include_groups - Include groups (default: false)
 * @param {boolean} params.active_groups - Include active groups only (default: false)
 * @param {boolean} params.excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of sorted users and groups
 */
const fetchFirmUserList = async ({
  status = 'ACTIVE',
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  keyWord = '',
  next = '',
  is_include_contacts = false,
  is_detailed = false,
  is_confirmed_user = true,
  is_include_groups = false,
  active_groups = false,
  excludeFromLoading = false,
}) => {
  try {
    const response = await listUsers({
      status,
      limit,
      pagination_token,
      pagination_direction,
      keyWord,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      active_groups,
      excludeFromLoading,
    });

    // Filter and sort same as useFirmUserList
    const firmUsers = response?.data?.firm_users?.filter((user) => user.res_type === 'user') || [];
    const groups = response?.data?.firm_users?.filter((user) => user.res_type === 'user-group') || [];
    const sortedFirmUsers = sortByKey(firmUsers, 'first_name');
    const sortedGroups = groups.sort((a, b) => (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || ''));

    return [...sortedFirmUsers, ...sortedGroups];
  } catch (error) {
    console.error('Error fetching firm user list:', error);
    throw error;
  }
};

/**
 * Hook to fetch firm users with React Query
 * Always loads the full list by getting total count first (same as useFirmUserList)
 * All query parameters are dynamic and can be customized per module
 *
 * @param {Object} options - Configuration options
 * @param {string} options.status - User status filter (default: 'ACTIVE')
 * @param {number} options.limit - Limit for fetching users (default: uses total count if not provided)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} options.keyWord - Search keyword (default: '')
 * @param {string} options.next - Next page token (default: '')
 * @param {boolean} options.is_include_contacts - Include contacts (default: false)
 * @param {boolean} options.is_detailed - Include detailed info (default: false)
 * @param {boolean} options.is_confirmed_user - Filter confirmed users (default: true)
 * @param {boolean} options.is_include_groups - Include groups (default: false)
 * @param {boolean} options.active_groups - Include active groups only (default: false)
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {boolean} options.loadFullList - If true, gets total count first (default: true)
 *
 * @returns {Object} - userList, isFirmUserLoading, error, refetch, isFetching
 */
export const useFirmUserListQuery = ({
  status = 'ACTIVE',
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  keyWord = '',
  next = '',
  is_include_contacts = false,
  is_detailed = false,
  is_confirmed_user = true,
  is_include_groups = false,
  active_groups = false,
  excludeFromLoading = false,
  enabled = true,
  loadFullList = true,
} = {}) => {
  // Get total count first (same as useFirmUserList) - only if loadFullList is true and limit is not provided
  const { data: countData } = useQuery({
    queryKey: ['user-count', status],
    queryFn: async () => {
      const countResponse = await totalCountEvent('user');
      return countResponse?.data || 10;
    },
    enabled: enabled && loadFullList && !limit, // Only fetch count if loadFullList is true and limit is not provided
    staleTime: 30 * 60 * 1000, // Increased to 30 minutes - count rarely changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    // refetchOnMount is intentionally not set to false, allowing refetch of invalidated (stale) data
    // This ensures that when users are created/updated in Settings, reports/filters see changes immediately
    retry: 2, // Retry twice on failure
  });

  const pageLimit = limit || (loadFullList ? countData : 10) || 10;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.firmUsers.list(
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyWord,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      active_groups
    ),
    queryFn: () =>
      fetchFirmUserList({
        status,
        limit: pageLimit,
        pagination_token,
        pagination_direction,
        keyWord,
        next,
        is_include_contacts,
        is_detailed,
        is_confirmed_user,
        is_include_groups,
        active_groups,
        excludeFromLoading,
      }),
    enabled: enabled && (limit || !loadFullList || !!countData), // Only fetch when limit is provided, or loadFullList is false, or count is available
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (React Query v4 uses gcTime instead of cacheTime)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    // refetchOnMount is intentionally not set to false, allowing refetch of invalidated (stale) data
    // This ensures that when users/groups are created/updated in Settings, reports/filters see changes immediately
    retry: 2, // Retry twice on failure
  });

  return {
    userList: data || [],
    isFirmUserLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
